/**
 * TanStack Query Keys Factory
 *
 * Centralized, type-safe query key management.
 * Prevents typos, ensures consistency, and enables better cache invalidation.
 *
 * @example
 * ```typescript
 * // Use in queries
 * queryKey: queryKeys.cart.detail(orderId)
 *
 * // Invalidate related queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.cart.all })
 * ```
 */

/**
 * Query keys factory - type-safe query key generation
 */

/**
 * Admin orders request params (defined locally as not in generated types)
 */
interface GetAllOrdersAdminRequest {
  page?: number;
  limit?: number;
  status?: string;
  payment_status?: string;
  search?: string;
}

export const queryKeys = {
  /**
   * Cart-related query keys
   */
  cart: {
    all: ['cart'] as const,
    detail: (orderId: string | null) => ['cart', orderId] as const,
    count: (orderId: string | null) => ['cart-count', orderId] as const,
  },

  /**
   * Order-related query keys
   */
  order: {
    all: ['order'] as const,
    detail: (orderId: string) => ['orders', 'detail', orderId] as const,
    details: (orderId: string) => ['order-details', orderId] as const,
    lookup: (orderId: string) => ['order-lookup', orderId] as const,
    byEmail: (email: string | null) => ['orders-by-email', email] as const,
    bySession: (email: string) => ['orders-by-session', email] as const,
    total: (orderId: string) => ['order-total', orderId] as const,
  },

  /**
   * Countries query keys
   */
  countries: {
    all: ['countries'] as const,
    supported: ['countries', 'supported'] as const,
  },

  /**
   * Admin query keys
   */
  admin: {
    all: ['admin'] as const,
    orders: {
      all: ['admin-orders'] as const,
      list: (params: { page?: number; limit?: number; status?: string; search?: string }) =>
        ['admin-orders', params.page, params.limit, params.status, params.search] as const,
      infinite: (params: GetAllOrdersAdminRequest) => ['admin-orders-infinite', params] as const,
    },
    books: {
      all: ['admin-books'] as const,
      list: (params: { page?: number; limit?: number }) =>
        ['admin-books', params.page, params.limit] as const,
    },
    categories: {
      all: ['admin-categories'] as const,
      list: (params: { page?: number; limit?: number }) =>
        ['admin-categories', params.page, params.limit] as const,
    },
    styles: {
      all: ['admin-styles'] as const,
      list: (params: { page?: number; limit?: number }) =>
        ['admin-styles', params.page, params.limit] as const,
    },
    personalities: {
      all: ['admin-personalities'] as const,
      list: (params: { page?: number; limit?: number }) =>
        ['admin-personalities', params.page, params.limit] as const,
    },
    sizes: {
      all: ['admin-sizes'] as const,
      list: (params: { page?: number; limit?: number }) =>
        ['admin-sizes', params.page, params.limit] as const,
    },
    statistics: ['admin-statistics'] as const,
    businessLogic: {
      all: ['admin-business-logic'] as const,
      list: (params: { page?: number; limit?: number; search?: string | undefined; active_only?: boolean; product_type?: string | undefined }) =>
        ['admin-business-logic', params] as const,
    },
  },

  /**
   * Preview and generation query keys
   */
  preview: {
    all: ['preview'] as const,
    image: (request: { orderId: string; productId?: string }) =>
      ['preview-image', request] as const,
  },

  /**
   * Gelato query keys
   */
  gelato: {
    all: ['gelato'] as const,
    quote: (request: { orderId: string }) => ['gelato-quote', request] as const,
    productInfo: (params: { productDefinitionId?: string | null; productCode?: string; countryCode?: string }) =>
      ['gelato-product-info', params] as const,
  },

  /**
   * Pricing query keys
   */
  pricing: {
    all: ['pricing'] as const,
    calculation: (request: { orderId: string }) =>
      ['fk-pricing-calculation', request] as const,
  },

  /**
   * Configuration and static data query keys
   */
  config: {
    all: ['config'] as const,
    supportedCountries: ['config', 'supportedCountries'] as const,
    colorsGrouped: ['colors-grouped'] as const,
    businessLogic: ['businessLogic'] as const,
  },

  /**
   * Order variants query keys
   */
  variants: {
    all: ['variants'] as const,
    order: (orderId: string) => ['variants', orderId] as const,
  },

  /**
   * Previous orders query keys
   */
  previousOrders: {
    all: ['previous-orders'] as const,
    summaries: (email: string) => ['previous-orders-summaries', email] as const,
  },

  /**
   * User-related query keys
   */
  user: {
    all: ['user'] as const,
    profile: (userId: string | null) => ['user', 'profile', userId] as const,
  },

  /**
   * Product catalog query keys
   */
  products: {
    all: ['products'] as const,
    detail: (productId: string) => ['products', 'detail', productId] as const,
    byCategory: (category: string) => ['products', category] as const,
  },

  /**
   * Studio query keys
   */
  studio: {
    all: ['studio'] as const,
    designs: ['studio-designs'] as const,
    stats: ['studio-stats'] as const,
  },

  /**
   * Order AI Content query keys
   */
  orderAiContent: {
    all: ['order-ai-content'] as const,
    byOrder: (orderId: string) => ['order_ai_content', orderId] as const,
  },

  /**
   * Photo-related query keys
   */
  photo: {
    all: ['photo'] as const,
    analysis: (orderId: string) => ['photo-analysis', orderId] as const,
    data: (orderId: string) => ['photo-data', orderId] as const,
  },
} as const;
